import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TTenantSelectProps } from '@/backoffice/features/tenants/components/tenant-select/tenant-select.types';
import { useReadTenantsForSuperTenantQuery } from '@/backoffice/redux/api/backoffice/backoffice-api';
import {
  BackofficeTenantFragment,
  ReadTenantsForSuperTenantQuery,
} from '@/backoffice/types/types.generated';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';

export const TenantSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  tenantFilter = undefined,
  disableTags = true,
  createOption,
  getItemId,
  additionalOptions = [],
  onSelectChange,
  required,
  rtkQueryArgs,
  multiple = true,
  ...rest
}: TTenantSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();

  const createDefaultOption = (tenant: BackofficeTenantFragment) =>
    ({
      label: tenant.companyName,
      value: tenant.tenantId,
      additionalInfo: [
        tenant.owner?.email,
        t(`countries:${tenant.country}`),
        t(`industries:${tenant.industry}`),
      ],
    }) as TSelectOption<TOptionShape>;

  const _createOption = createOption ?? createDefaultOption;

  const transformData = (data?: ReadTenantsForSuperTenantQuery) => {
    const tenants = data?.readTenantsForSuperTenant?.items ?? [];
    const validTenants = tenantFilter
      ? tenants.filter((tenant) => tenantFilter(tenant))
      : tenants;

    const options = validTenants.map((tenant) => _createOption(tenant));

    if (additionalOptions.length > 0) {
      return [...additionalOptions, ...options];
    }

    return options;
  };

  return (
    <AsyncSearchableSelect<
      TFieldValues,
      ReadTenantsForSuperTenantQuery,
      TOptionShape
    >
      name={name}
      label={t('pages:tenants')}
      disableTags={disableTags}
      multiple={multiple}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadTenantsForSuperTenantQuery,
        transformData,
        rtkQueryArgs,
        searchField: 'companyNameLower',
      }}
      onSelectChange={onSelectChange}
      required={required}
      {...rest}
    />
  );
};
